import {
  Box,
  Container,
  Heading,
  SimpleGrid,
  VStack,
  Text,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  List,
  ListItem,
  ListIcon,
  Link,
} from '@chakra-ui/react';
import { CheckCircleIcon } from '@chakra-ui/icons';
import { contactInfo, practiceAreas } from '../lib/storage';
import { useEffect, useRef, useState } from 'react';

const PracticeAreas: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );
    const section = sectionRef.current;
    if (section) {
      observer.observe(section);
    }

    return () => {
      if (section) {
        observer.unobserve(section);
      }
    };
  }, []);

  return (
    <Box
      id="practice-areas"
      ref={sectionRef}
      bg="alabasterGrey"
      py={{ base: 16, md: 24 }}
      position="relative"
    >
      <Container maxW="1400px">
        <SimpleGrid
          columns={{ base: 1, lg: 12 }}
          spacing={{ base: 8, lg: 12 }}
          alignItems="start"
        >
          <Box
            gridColumn={{ base: '1', lg: '1 / 5' }}
            opacity={isVisible ? 1 : 0}
            transform={isVisible ? 'translateX(0)' : 'translateX(-40px)'}
            transition="all 0.8s ease-out"
          >
            <VStack align="flex-start" spacing={5} position={{ lg: 'sticky' }} top="110px">
              <Text
                fontSize={{ base: 'sm', md: 'md' }}
                fontWeight={700}
                color="stormyTeal"
                letterSpacing="2px"
                textTransform="uppercase"
              >
                7 áreas de atuação
              </Text>
              <Heading
                as="h2"
                fontSize={{ base: '4xl', md: '5xl', lg: '6xl' }}
                color="yaleBlue"
                fontWeight={700}
                lineHeight={1.1}
              >
                Áreas de Atuação
              </Heading>
              <Text color="graphite" fontSize={{ base: 'md', md: 'lg' }} lineHeight="1.7">
                Atendimento técnico, direto e acessível para demandas previdenciárias,
                trabalhistas, bancárias, consumeristas, familiares, civis e de saúde.
              </Text>
            </VStack>
          </Box>

          <Box
            gridColumn={{ base: '1', lg: '5 / 13' }}
            opacity={isVisible ? 1 : 0}
            transform={isVisible ? 'translateX(0)' : 'translateX(40px)'}
            transition="all 0.8s ease-out 0.2s"
          >
            <VStack spacing={8} align="stretch">
              <Box bg="white" borderRadius="8px" boxShadow="sm" overflow="hidden">
                <Accordion allowToggle defaultIndex={[0]}>
                  {practiceAreas.map((area, index) => (
                    <AccordionItem
                      key={area.id}
                      border="none"
                      borderBottom={index < practiceAreas.length - 1 ? '1px solid' : 'none'}
                      borderColor="alabasterGrey"
                    >
                      <AccordionButton
                        py={6}
                        px={{ base: 6, md: 8 }}
                        _hover={{ bg: 'bone' }}
                        transition="all 0.3s ease"
                      >
                        <Box flex="1" textAlign="left">
                          <Text fontSize="sm" color="stormyTeal" fontWeight={700} mb={1}>
                            Área {area.id.padStart(2, '0')}
                          </Text>
                          <Text fontSize={{ base: 'lg', md: 'xl' }} fontWeight={700} color="yaleBlue">
                            {area.title}
                          </Text>
                        </Box>
                        <AccordionIcon color="stormyTeal" boxSize={6} />
                      </AccordionButton>
                      <AccordionPanel pb={8} px={{ base: 6, md: 8 }}>
                        <VStack align="stretch" spacing={6}>
                          <Text fontSize={{ base: 'md', md: 'lg' }} color="graphite" lineHeight="1.8">
                            {area.intro}
                          </Text>

                          <Box>
                            <Text fontWeight={700} color="yaleBlue" fontSize="lg" mb={4}>
                              Serviços desta área
                            </Text>
                            <List spacing={3}>
                              {area.services.map((service, idx) => (
                                <ListItem
                                  key={idx}
                                  display="flex"
                                  alignItems="flex-start"
                                  fontSize={{ base: 'sm', md: 'md' }}
                                  color="graphite"
                                  lineHeight="1.6"
                                >
                                  <ListIcon as={CheckCircleIcon} color="stormyTeal" mt="2px" flexShrink={0} />
                                  <Text>{service}</Text>
                                </ListItem>
                              ))}
                            </List>
                          </Box>

                          <Box bg="bone" p={5} borderRadius="8px">
                            <Text fontWeight={700} color="yaleBlue" mb={2}>
                              Para quem é esta área
                            </Text>
                            <Text color="graphite" lineHeight="1.7">
                              {area.audience}
                            </Text>
                          </Box>
                        </VStack>
                      </AccordionPanel>
                    </AccordionItem>
                  ))}
                </Accordion>
              </Box>

              <Box
                as="button"
                bg="yaleBlue"
                color="white"
                py={4}
                px={8}
                borderRadius="4px"
                fontWeight={600}
                fontSize="md"
                w={{ base: 'full', md: 'auto' }}
                alignSelf={{ base: 'stretch', md: 'flex-start' }}
                transition="all 0.3s ease"
                _hover={{ bg: 'taupe', transform: 'translateY(-2px)', boxShadow: 'lg' }}
                onClick={() => {
                  const element = document.getElementById('contact');
                  if (element) {
                    const yOffset = -80;
                    const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
                    window.scrollTo({ top: y, behavior: 'smooth' });
                  }
                }}
              >
                <Link href={contactInfo.link} _hover={{ textDecoration: 'none', color: 'white' }}>
                  Agende uma Consulta
                </Link>
              </Box>
            </VStack>
          </Box>
        </SimpleGrid>
      </Container>
    </Box>
  );
};

export default PracticeAreas;

